import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { initSounds, playSoundIfEnabled } from '../utils/soundEffects';
import SoundToggle from './SoundToggle';

const { FiShuffle, FiArrowLeft, FiHeart, FiFilter, FiRefreshCw, FiInfo } = FiIcons;

const CardGame = () => {
  const navigate = useNavigate();
  const [currentCard, setCurrentCard] = useState(null);
  const [usedCards, setUsedCards] = useState([]);
  const [isFlipped, setIsFlipped] = useState(false);
  const [filter, setFilter] = useState('all'); // 'all', 'mild', 'spicy', 'hot', 'extreme'
  const [isLoading, setIsLoading] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [dynamicCards, setDynamicCards] = useState([]);

  // Initialize sounds when component mounts
  useEffect(() => {
    initSounds();
  }, []);

  // Generate a random card text based on category and level
  const generateCardText = (category, level) => {
    const levelTexts = {
      mild: {
        "Massage": [
          "Massage vai và cổ cho người kia trong 2 phút với tinh dầu",
          "Massage tay cho người kia trong 1 phút",
          "Massage đầu cho người kia trong 3 phút",
          "Massage lưng nhẹ nhàng cho người kia",
          "Massage mặt thư giãn cho người kia"
        ],
        "Kiss": [
          "Hôn lên trán và thì thầm điều gì đó ngọt ngào vào tai",
          "Hôn nhẹ lên má người kia 3 lần",
          "Hôn tay người kia như một quý ông/quý cô",
          "Hôn người kia và kể một điều bạn thích về họ",
          "Hôn lên mũi người kia và ôm họ thật chặt"
        ],
        "Romance": [
          "Nói 3 điều bạn thích nhất về người kia",
          "Hát một bài hát tình yêu ngắn cho người kia",
          "Kể một kỷ niệm đẹp giữa hai người",
          "Nhảy chậm với người kia trong 1 phút",
          "Viết một bài thơ tình ngắn cho người kia"
        ],
        "Sweet": [
          "Ôm người kia thật chặt trong 30 giây",
          "Vẽ một trái tim lên lưng người kia bằng ngón tay",
          "Thì thầm lời yêu thương vào tai người kia",
          "Cho người kia ăn một món ngọt",
          "Mô tả người kia bằng 3 từ ngọt ngào nhất"
        ]
      },
      spicy: {
        "Kiss": [
          "Hôn và cắn nhẹ tai người kia trong 1 phút",
          "Hôn môi say đắm trong 2 phút không được dừng",
          "Hôn cổ người kia trong 30 giây",
          "Hôn từ cổ lên tai rồi xuống vai",
          "Hôn và liếm nhẹ môi người kia"
        ],
        "Touch": [
          "Dùng tay vuốt ve toàn thân người kia trong 2 phút",
          "Dùng lụa hoặc khăn mềm vuốt ve toàn bộ cơ thể người kia trong 3 phút",
          "Để người kia đặt tay lên cơ thể bạn ở bất cứ đâu họ muốn trong 3 phút",
          "Dùng đầu ngón tay vẽ những đường cong trên cơ thể người kia",
          "Nhắm mắt và để người kia chạm vào bạn trong 1 phút"
        ],
        "Dance": [
          "Nhảy một điệu nhảy quyến rũ trong 30 giây",
          "Nhảy lap dance nhẹ nhàng cho người kia trong 1 phút",
          "Nhảy và cởi một món phụ kiện",
          "Nhảy sexy theo một bài hát do người kia chọn",
          "Dạy người kia một điệu nhảy gợi cảm"
        ],
        "Secret": [
          "Chia sẻ một ảo tưởng tình dục mà bạn chưa từng nói với ai",
          "Kể về giấc mơ 18+ gần đây nhất của bạn",
          "Thì thầm vào tai người kia 3 điều bạn muốn làm với họ trong phòng ngủ",
          "Mô tả điều bạn muốn người kia làm cho bạn ngay bây giờ",
          "Kể về trải nghiệm tình dục đáng nhớ nhất của bạn"
        ]
      },
      hot: {
        "Strip": [
          "Cởi một món đồ một cách quyến rũ nhất có thể",
          "Cởi áo của người kia bằng răng",
          "Thực hiện striptease với nhạc trong 2 phút",
          "Tự cởi đồ một cách quyến rũ nhất có thể trong khi nhìn thẳng vào mắt người kia",
          "Cởi đồ của người kia chỉ bằng miệng trong 2 phút"
        ],
        "Lick": [
          "Liếm kem từ cơ thể người kia",
          "Dùng lưỡi liếm từ cổ xuống ngực người kia",
          "Dùng lưỡi mát-xa cổ và tai người kia trong 2 phút",
          "Liếm và cắn nhẹ từ cổ xuống rốn của người kia",
          "Liếm một đường từ thắt lưng lên giữa lưng người kia"
        ],
        "Role Play": [
          "Đóng vai bác sĩ và 'khám' cho bệnh nhân của bạn",
          "Đóng vai giáo viên nghiêm khắc và học sinh hư",
          "Đóng vai người lạ và quyến rũ đối phương như lần đầu gặp gỡ",
          "Đóng vai người phục vụ và đáp ứng mọi nhu cầu của khách hàng",
          "Đóng vai thám tử và 'khám xét' người tình nghi"
        ],
        "Tease": [
          "Tự vuốt ve cơ thể mình trong khi người kia xem",
          "Vuốt ve cơ thể mình trong khi mô tả những gì bạn đang cảm nhận",
          "Giả vờ đạt cực khoái một cách thuyết phục nhất có thể",
          "Mô phỏng quan hệ với người kia khi cả hai vẫn mặc quần áo trong 1 phút",
          "Tạo dáng gợi cảm nhất có thể trong 1 phút"
        ]
      },
      extreme: {
        "Nóng Bỏng": [
          "Dùng miệng khám phá bộ phận nhạy cảm của người kia trong 3 phút",
          "Dùng miệng khám phá vùng nhạy cảm của người kia mà không dùng tay",
          "Để người kia dùng miệng tạo khoái cảm cho bạn trong 2 phút",
          "Dùng miệng làm người kia thỏa mãn đến khi họ yêu cầu dừng lại",
          "Hôn và liếm toàn bộ cơ thể người kia từ trên xuống dưới"
        ],
        "Dâm Đãng": [
          "Đặt người kia lên đùi và 'trừng phạt' bằng tay trong 2 phút",
          "Quỳ xuống và phục vụ người kia theo cách họ yêu cầu trong 3 phút",
          "Tự làm mình thỏa mãn trong khi người kia chỉ được nhìn không được chạm vào",
          "Tự làm mình cực khoái trong khi người kia chỉ được nhìn, không được chạm vào",
          "Tự sướng trong khi người kia thì thầm những lời khiêu khích vào tai bạn"
        ],
        "Khám Phá": [
          "Bịt mắt và để người kia dùng lưỡi khám phá toàn bộ cơ thể bạn",
          "Để người kia buộc mắt bạn và làm bất cứ điều gì họ muốn trong 5 phút",
          "Dùng một món đồ trong phòng để kích thích người kia (không phải đồ chơi người lớn)",
          "Dùng đá lạnh để kích thích vùng nhạy cảm của người kia",
          "Thử một tư thế quan hệ mà hai người chưa từng thử trước đây"
        ],
        "Thống Trị": [
          "Trói tay người kia và làm bất cứ điều gì bạn muốn trong 5 phút",
          "Trói tay người kia và 'tra tấn' họ bằng những nụ hôn nhẹ trong 3 phút",
          "Ra lệnh cho người kia phải làm 3 điều bạn muốn trong 5 phút tới",
          "Cho người kia làm bất cứ điều gì họ muốn trong 5 phút",
          "Để người kia trói tay bạn và làm điều họ muốn"
        ]
      }
    };

    // Get the category texts for the selected level
    const categoryTexts = levelTexts[level] ? levelTexts[level][category] : null;
    if (!categoryTexts) return "Không tìm thấy thẻ phù hợp";

    // Return a random text from the category
    return categoryTexts[Math.floor(Math.random() * categoryTexts.length)];
  };

  // Generate a set of dynamic cards on component mount and when reset
  const generateDynamicCardSet = useCallback(() => {
    const newCards = [];
    const categories = {
      mild: ["Massage", "Kiss", "Romance", "Sweet"],
      spicy: ["Kiss", "Touch", "Dance", "Secret"],
      hot: ["Strip", "Lick", "Role Play", "Tease"],
      extreme: ["Nóng Bỏng", "Dâm Đãng", "Khám Phá", "Thống Trị"]
    };

    // Generate 10 cards for each level
    Object.keys(categories).forEach(level => {
      const levelCategories = categories[level];
      for (let i = 0; i < 10; i++) {
        const randomCategory = levelCategories[Math.floor(Math.random() * levelCategories.length)];
        newCards.push({
          category: randomCategory,
          text: generateCardText(randomCategory, level),
          level: level
        });
      }
    });

    return newCards;
  }, []);

  useEffect(() => {
    // Generate initial dynamic cards
    setDynamicCards(generateDynamicCardSet());
  }, [generateDynamicCardSet]);

  // Static cards as a base
  const staticCards = [
    // Existing cards
    { category: "Massage", text: "Massage vai và cổ cho người kia trong 2 phút với tinh dầu", level: "mild" },
    { category: "Kiss", text: "Hôn lên trán và thì thầm điều gì đó ngọt ngào vào tai", level: "mild" },
    { category: "Dance", text: "Nhảy một điệu nhảy quyến rũ trong 30 giây", level: "spicy" },
    { category: "Massage", text: "Massage chân cho đối phương và hôn lên mỗi ngón chân", level: "spicy" },
    { category: "Strip", text: "Cởi một món đồ một cách quyến rũ nhất có thể", level: "hot" },
    { category: "Kiss", text: "Hôn từ cổ xuống ngực trong 1 phút", level: "hot" },
    { category: "Role Play", text: "Đóng vai bác sĩ và 'khám' cho bệnh nhân của bạn", level: "hot" },
    { category: "Massage", text: "Massage toàn thân với dầu nóng trong 5 phút", level: "hot" },
    { category: "Dare", text: "Làm 10 cái hít đất với trang phục hiện tại", level: "mild" },
    { category: "Truth", text: "Kể về giấc mơ 18+ gần đây nhất của bạn", level: "spicy" }
  ];

  const generateDynamicCard = () => {
    // Combine all available cards
    const allCards = [...staticCards, ...dynamicCards];

    // Filter by level if needed
    const filteredCards = filter === 'all' ? allCards : allCards.filter(card => card.level === filter);

    // Get cards that haven't been used yet
    const unusedCards = filteredCards.filter(card => !usedCards.includes(allCards.indexOf(card)));

    if (unusedCards.length === 0) {
      // If all cards are used, regenerate a new set of dynamic cards
      const newDynamicCards = generateDynamicCardSet();
      setDynamicCards(newDynamicCards);

      // Reset used cards
      setUsedCards([]);

      // Return a random card from the new set that matches the filter
      const newAllCards = [...staticCards, ...newDynamicCards];
      const newFilteredCards = filter === 'all' ? newAllCards : newAllCards.filter(card => card.level === filter);
      const randomIndex = Math.floor(Math.random() * newFilteredCards.length);
      return newFilteredCards[randomIndex];
    }

    // Select a random card from unused cards
    const randomIndex = Math.floor(Math.random() * unusedCards.length);
    const selectedCard = unusedCards[randomIndex];

    // Mark this card as used
    const originalIndex = allCards.indexOf(selectedCard);
    setUsedCards(prev => [...prev, originalIndex]);

    return selectedCard;
  };

  const drawCard = () => {
    setIsFlipped(false);
    setIsLoading(true);
    
    // Play card shuffle sound
    playSoundIfEnabled('cardShuffle');
    
    setTimeout(() => {
      const newCard = generateDynamicCard();
      setCurrentCard(newCard);
      
      // Play card flip sound when revealing
      playSoundIfEnabled('cardFlip');
      
      setIsFlipped(true);
      setIsLoading(false);
    }, 500);
  };

  const resetCards = () => {
    setUsedCards([]);
    // Play sound for resetting
    playSoundIfEnabled('notification');
    
    // Generate a completely new set of dynamic cards
    setDynamicCards(generateDynamicCardSet());
    drawCard();
  };

  const getLevelColor = (level) => {
    switch (level) {
      case 'mild': return 'from-green-400 to-blue-400';
      case 'spicy': return 'from-yellow-400 to-orange-400';
      case 'hot': return 'from-red-400 to-pink-500';
      case 'extreme': return 'from-purple-600 to-red-600';
      default: return 'from-gray-400 to-gray-500';
    }
  };

  const getLevelEmoji = (level) => {
    switch (level) {
      case 'mild': return '😊';
      case 'spicy': return '😏';
      case 'hot': return '🔥';
      case 'extreme': return '💦';
      default: return '❤️';
    }
  };

  const getFilteredCardsCount = (filterLevel) => {
    const allCards = [...staticCards, ...dynamicCards];
    return filterLevel === 'all' ? allCards.length : allCards.filter(card => card.level === filterLevel).length;
  };

  const getUsedCardsCount = (filterLevel) => {
    const allCards = [...staticCards, ...dynamicCards];
    if (filterLevel === 'all') return usedCards.length;

    return usedCards.filter(index => {
      if (index >= allCards.length) return false;
      return allCards[index].level === filterLevel;
    }).length;
  };

  const handleFilterChange = (newFilter) => {
    // Play click sound when changing filter
    playSoundIfEnabled('click');
    setFilter(newFilter);
  };

  if (showPrivacy) {
    return (
      <div className="min-h-screen p-4 flex flex-col">
        <motion.button
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => setShowPrivacy(false)}
          className="self-start mb-4 bg-white/20 hover:bg-white/30 text-white p-3 rounded-full transition-all"
        >
          <SafeIcon icon={FiArrowLeft} className="text-xl" />
        </motion.button>

        <div className="flex-1 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl w-full bg-white/10 backdrop-blur-lg p-8 rounded-3xl"
          >
            <h1 className="text-3xl font-bold text-white text-center mb-6">
              Điều Khoản & Quyền Riêng Tư
            </h1>

            <div className="space-y-6 text-white">
              <div>
                <h2 className="text-xl font-bold mb-2">1. Giới Hạn Độ Tuổi:</h2>
                <p className="text-pink-200">
                  Ứng dụng này chỉ dành cho người từ 18 tuổi trở lên. Bằng việc sử dụng ứng dụng này, bạn xác nhận mình đã đủ tuổi theo quy định.
                </p>
              </div>

              <div>
                <h2 className="text-xl font-bold mb-2">2. Quyền Sở Hữu:</h2>
                <p className="text-pink-200">
                  Game này được tạo và sở hữu độc quyền bởi Lux Homestay. Mọi hình thức sao chép, phân phối lại mà không có sự cho phép đều bị nghiêm cấm.
                </p>
              </div>

              <div>
                <h2 className="text-xl font-bold mb-2">3. Nội Dung:</h2>
                <p className="text-pink-200">
                  Trò chơi có chứa nội dung người lớn và chỉ nên chơi trong không gian riêng tư. Người dùng tự chịu trách nhiệm về việc sử dụng trò chơi này.
                </p>
              </div>

              <div>
                <h2 className="text-xl font-bold mb-2">4. Quyền Riêng Tư:</h2>
                <p className="text-pink-200">
                  Chúng tôi không thu thập, lưu trữ hay chia sẻ bất kỳ thông tin cá nhân nào của người dùng. Trò chơi được chơi hoàn toàn ngoại tuyến.
                </p>
              </div>

              <div>
                <h2 className="text-xl font-bold mb-2">5. Miễn Trừ Trách Nhiệm:</h2>
                <p className="text-pink-200">
                  Lux Homestay không chịu trách nhiệm về bất kỳ hậu quả nào phát sinh từ việc sử dụng trò chơi này. Người dùng tự chịu trách nhiệm về quyết định và hành động của mình.
                </p>
              </div>

              <div className="pt-4 border-t border-white/20">
                <p className="text-center text-pink-200">
                  © 2023 Lux Homestay. Bảo lưu mọi quyền.
                </p>
              </div>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  playSoundIfEnabled('click');
                  setShowPrivacy(false);
                }}
                className="w-full mt-4 bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white font-bold py-4 px-6 rounded-2xl shadow-lg transition-all duration-300"
              >
                Tôi Đã Hiểu & Đồng Ý
              </motion.button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 flex flex-col">
      <div className="flex justify-between items-center mb-4">
        <motion.button
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => {
            playSoundIfEnabled('click');
            navigate('/');
          }}
          className="bg-white/20 hover:bg-white/30 text-white p-3 rounded-full transition-all"
        >
          <SafeIcon icon={FiArrowLeft} className="text-xl" />
        </motion.button>
        
        <SoundToggle className="bg-white/20" />
      </div>

      <div className="flex-1 flex items-center justify-center">
        <div className="max-w-md w-full">
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl font-bold text-white text-center mb-6"
          >
            💕 Thẻ Bài Tình Yêu 💕
          </motion.h1>

          <div className="mb-6">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex flex-wrap justify-center gap-2"
            >
              {['all', 'mild', 'spicy', 'hot', 'extreme'].map((level) => (
                <motion.button
                  key={level}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handleFilterChange(level)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    filter === level
                      ? `bg-gradient-to-r ${getLevelColor(level === 'all' ? 'hot' : level)} text-white`
                      : 'bg-white/10 text-white/80 hover:bg-white/20'
                  }`}
                >
                  {level === 'all' ? 'Tất Cả' : level === 'mild' ? 'Nhẹ Nhàng' : level === 'spicy' ? 'Cay' : level === 'hot' ? 'Nóng' : 'Cực Nóng'} {getLevelEmoji(level === 'all' ? 'hot' : level)}
                </motion.button>
              ))}
            </motion.div>
          </div>

          <AnimatePresence mode="wait">
            {currentCard && !isLoading && (
              <motion.div
                key={currentCard.text}
                initial={{ opacity: 0, rotateY: -90, scale: 0.8 }}
                animate={{ opacity: isFlipped ? 1 : 0, rotateY: isFlipped ? 0 : -90, scale: isFlipped ? 1 : 0.8 }}
                exit={{ opacity: 0, rotateY: 90, scale: 0.8 }}
                transition={{ duration: 0.4 }}
                className={`
                  bg-gradient-to-br ${getLevelColor(currentCard.level)} p-8 rounded-3xl shadow-2xl text-white relative overflow-hidden min-h-[300px] flex flex-col justify-center
                `}
              >
                <div className="absolute top-4 right-4 text-2xl">
                  {getLevelEmoji(currentCard.level)}
                </div>

                <div className="absolute top-4 left-4 bg-white/20 px-3 py-1 rounded-full">
                  <span className="text-sm font-semibold">{currentCard.category}</span>
                </div>

                <div className="text-center">
                  <p className="text-xl leading-relaxed font-medium">
                    {currentCard.text}
                  </p>
                </div>

                <div className="absolute bottom-0 left-0 w-full h-2 bg-white/20" />
              </motion.div>
            )}

            {isLoading && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="min-h-[300px] flex items-center justify-center"
              >
                <div className="w-16 h-16 border-t-4 border-pink-500 rounded-full animate-spin"></div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="flex gap-4 mt-8">
            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={drawCard}
              disabled={isLoading}
              className="
                flex-1 bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 disabled:from-gray-500 disabled:to-gray-600 disabled:cursor-not-allowed text-white font-bold py-4 px-4 rounded-2xl shadow-lg flex items-center justify-center gap-2 text-lg transition-all duration-300
              "
            >
              <SafeIcon icon={FiShuffle} className="text-xl" />
              Rút Thẻ Mới
            </motion.button>

            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={resetCards}
              disabled={isLoading}
              className="
                bg-white/20 hover:bg-white/30 disabled:bg-white/10 disabled:cursor-not-allowed text-white font-bold py-4 px-4 rounded-2xl flex items-center justify-center transition-all duration-300
              "
              title="Làm mới toàn bộ bộ bài"
            >
              <SafeIcon icon={FiRefreshCw} className="text-xl" />
            </motion.button>
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-center mt-6"
          >
            <p className="text-pink-200 text-sm">
              Đã sử dụng {getUsedCardsCount(filter)}/{getFilteredCardsCount(filter)} thẻ
              {filter !== 'all'
                ? ` ${
                    filter === 'mild'
                      ? 'nhẹ nhàng'
                      : filter === 'spicy'
                      ? 'cay'
                      : filter === 'hot'
                      ? 'nóng'
                      : 'cực nóng'
                  }`
                : ''}
            </p>
            <div className="w-full bg-white/20 rounded-full h-2 mt-2">
              <div
                className={`bg-pink-400 h-2 rounded-full transition-all duration-300`}
                style={{ width: `${(getUsedCardsCount(filter) / getFilteredCardsCount(filter)) * 100}%` }}
              />
            </div>
          </motion.div>

          <div className="flex items-center justify-between mt-6">
            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              onClick={() => {
                playSoundIfEnabled('click');
                setShowPrivacy(true);
              }}
              className="text-pink-200 text-xs underline flex items-center"
            >
              <SafeIcon icon={FiInfo} className="mr-1 text-xs" />
              Điều khoản & Quyền riêng tư
            </motion.button>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="text-pink-200 text-xs"
            >
              © 2023 Lux Homestay
            </motion.p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CardGame;